// @ts-strict-ignore
import * as React from 'react';
import isArray from 'lodash/isArray';

/**
 * Filter menu: multi-screen menu to select a cohort or property filter
 */
import {getPrefixForBehavior} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {preventTracking, propagateEvent} from 'common/util/dom';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {
  DisplayContext,
  FilterOperand,
  ListOperator,
  NthTimeOperator,
} from 'common/widgets/property-filter-menu/models/types';
import {isFirstTimeEventFilter, PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {MappedPropertyValues, PropertyType, ResourceType} from 'common/report/constants';
import {reporter} from 'common/report/error-reporting';
import {dataGroupsClient} from 'common/report/queries/app-api/data-groups-client';
import {cohortsStore} from 'common/report/stores/cohorts';
import {syncReportPropertyValueMappings} from 'common/report/util/mappings';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {propertiesUtils} from 'common/report/util/properties';
import {getPropertyPath, getPropertyPathPrefix} from 'common/query-builder/query-block/utils/property-util';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {PropertyValuesScreenReact} from 'common/widgets/property-filter-menu/property-values-screen';
import {withStyles} from 'common/util/react';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {IronContextRegistry} from 'common/report/context-registry';
import {Translatable} from 'common/i18n';
import {useTranslation} from 'common/i18n/hooks';

import {ChildMenuInterface} from '../child-menu';
import {PropertySectionItem} from '../properties-menu/item-menu-types';
// eslint-disable-next-line import/no-cycle
import {PropertiesMenu, PropertiesMenuReact} from '../properties-menu';
import {CohortSegment} from '../segment-menu/types';
import {getCohortFromPropertyItem} from '../utils/util';
import {getSingleDataGroupId} from '../utils/data-group-util';
import {
  IncludeCustomPropertyType,
  isProperty,
  MenuItemType,
  Property,
  SelectedPropertyItem,
  TypecastPillAction,
} from '../properties-menu/types';
import {getSelectedPropertyLabel} from '../properties-menu/util';
import {CohortSelection} from './cohort-selection';
import {FilterMenuProps, FilterMenuState} from './types';
import css from './index.inline.styl';
import {
  generateFilterTrackingProps,
  getCohortFilterLabelParts,
  getCohortGroupLabel,
  transformFilterToPropertyItem,
} from './util';

// Terminology may not be/remain the same as Cohort operators so we remap
// the labels
const COHORT_FILTER_OPERATOR_LABELS = {
  [ListOperator.Contains]: `in`,
  [ListOperator.DoesNotContain]: `not in`,
};

const LOADING_NOTICE = `Loading...`;

// based on if the state variable stagedPropertyFilter is not, filter-menu can have one of the 2
// kinds of child nodes underneath: if stagedPropertyFilter is not set, we have properties-menu, for
// the user to pick a property, which gets set to stagedPropertyFilter, which pushes filter-menu to
// the next stage, with mp-property-values-screen as the child. we need to make sure properties-menu
// is the one under the filter-menu instance at all times except only when the filter-menu is open
// and the user has selected a property. If properties-menu is not the child not when the
// filter-menu is closed, for example, we miss any callbacks going to the properties-menu to update
// the state, leaving an inconsistent UI.
// TODO: the above makes the state keeping of filter-menu subtle and error-prone. Can we do better?
// Is having properties-menu always in the DOM (but not always visible) a viable option?
// TODO(chi): add tests for the different consistency cases of custom properties

export class FilterMenu
  extends React.PureComponent<FilterMenuProps & Pick<IronContextRegistry, `currentProject` | `i18n`>, FilterMenuState>
  implements ChildMenuInterface
{
  private propertiesMenuRef = React.createRef<PropertiesMenu>();

  static defaultProps = {
    includeCustomProperties: IncludeCustomPropertyType.None,
    displayContext: DisplayContext.Default,
    disabledItems: {},
    otherFilterOptions: [],
  };

  state = {
    dataGroups: [] as DataGroup[],
    selectedCohortFilterOption: null as ListOperator,
    stagedPropertyFilter: null as PropertyFilter,
    syncedPropertyValueMappings: false,
  };

  componentDidUpdate(prevProps: Readonly<FilterMenuProps>): void {
    if (this.props.open === prevProps.open) {
      this.props.onRender?.();
      return;
    }
    if (this.props.open) {
      this.updateFromAttributes();
    } else {
      this.setState({
        selectedCohortFilterOption: null,
        stagedPropertyFilter: null,
      });
    }
    this.props.onRender?.();
  }

  getSelectedPropertyFilter = () => {
    return (
      this.state.stagedPropertyFilter?.propertyName &&
      this.state.stagedPropertyFilter?.resourceType !== ResourceType.Cohort
    );
  };

  isCohortSelection = () => {
    return this.propertiesMenuRef.current?.state.selectedFilter?.resourceType === ResourceType.Cohort;
  };

  onPropertyFilterBackClick = () => this.setState({stagedPropertyFilter: null});

  onPropertyFilterChange = (ev) => this.setState({stagedPropertyFilter: ev.detail.propertyFilter});

  onPropertyFilterCommit = (ev) => this.onPropertyFilterSelected(ev.detail.propertyFilter);

  /** Updates the staged cohort filter operator */
  onCohortFilterOptionChange = (operator: ListOperator) => this.setState({selectedCohortFilterOption: operator});

  propagateEvent = (ev) => propagateEvent(this, ev);

  /** Handles tab change from underlying mp-properties-menu */
  handlePropertySelectOption = () => {
    let selectedCohortFilterOption = this.state.selectedCohortFilterOption;
    const stagedFilter = this.state.stagedPropertyFilter;
    if (
      stagedFilter &&
      (isArray(stagedFilter.filterValue && (stagedFilter.filterValue as FilterOperand[]).length > 1) ||
        stagedFilter.filterOperator !== ListOperator.DoesNotContain)
    ) {
      /** Triggers multi-select */
      selectedCohortFilterOption = ListOperator.Contains;
    }
    /** Always ensure update call on tab change */
    this.setState({selectedCohortFilterOption});
  };

  handleStageChange = () => this.forceUpdate();
  handleTabChange = () => this.forceUpdate();

  /** Stages the selected property or cohort */
  handlePropertySelect = (propertyItem: SelectedPropertyItem) => {
    const item = propertyItem.item as PropertySectionItem;
    const isCohort = Array.isArray(item) || item.resourceType === ResourceType.Cohort;

    // clear staged property filter if no cohorts are selected
    if (isCohort) {
      if (Array.isArray(item) && !item.length) {
        this.setState({stagedPropertyFilter: null});
      } else {
        this.handleCohortSelect(item, {...propertyItem} as any);
      }
    } else {
      this.handleEventPropertySelect(item, {...propertyItem} as any);
    }
  };

  /** Provides a selection value for the underlying menu */
  getMenuSelection = () => transformFilterToPropertyItem(this.selected);

  componentDidMount() {
    this.fetchCohorts();
    this.fetchDataGroups();
    this.syncPropertyValueMappings();
    this.updateFromAttributes();
  }

  get selected() {
    return this.props.open ? this.state.stagedPropertyFilter : this.props.selected;
  }

  get sourceAttrs() {
    return {
      projectId: Number(this.props.projectId || getProjectId()),
      workspaceId: Number(this.props.workspaceId || getWorkspaceId()),
    };
  }

  get displayContext() {
    return this.props.displayContext || DisplayContext.Default;
  }

  /**
   * Sets the selection state from the selected filter
   */
  public updateFromAttributes() {
    const selected = this.props.selected || {};
    const selectionType = selected && selected.resourceType;
    const isCohort = selectionType === ResourceType.Cohort;
    const selectedCohortFilterOption = isCohort ? selected.filterOperator : null;

    this.setState({
      selectedCohortFilterOption,
      stagedPropertyFilter: selectionType ? new PropertyFilter(selected) : null,
    });
  }

  public selectedLeftIcon() {
    const selected = this.props.selected;

    switch (selected && selected.resourceType) {
      case ResourceType.User:
        return selected.dataGroupId ? `group-attribute` : `profile-attribute`;
      case ResourceType.Event:
        return `event-attribute`;
      case ResourceType.Cohort:
        return `community`;
      default:
        return null;
    }
  }

  public isUnsavedCustomEntity() {
    const selected = this.props.selected;

    switch (selected?.resourceType) {
      case ResourceType.Cohort:
        //If there's a raw_cohort property then this is unsaved
        return Boolean(selected.filterValue[0].cohort.raw_cohort);
      case ResourceType.User:
      case ResourceType.Event: {
        //If customPropertyId is a string with temp then it's not saved
        const customPropId = selected.customPropertyId;
        return customPropId === undefined ? false : !isPersistedCustomProperty(customPropId);
      }
      default:
        return null;
    }
  }

  public getPrefixLabel() {
    if (this.selected && `behavior` in this.selected && this.selected.behavior) {
      return getPrefixForBehavior(this.selected.behavior);
    } else if (this.selected?.resourceType !== ResourceType.Cohort) {
      return getPropertyPathPrefix(
        transformFilterToPropertyItem(this.selected) as Property,
        this.props.i18n,
        this.state.dataGroups,
        {
          includeResourceLabel: Boolean(this.props.listProperty) === false,
        },
      );
    }

    return null;
  }

  public selectedLabel(): Translatable {
    if (this.selected) {
      if (this.selected.filterOperator === NthTimeOperator.NthTime) {
        return ({t}) => t(`query-builder.first-time-ever`);
      }

      if (
        Object.values(MappedPropertyValues).includes(this.selected.propertyName) &&
        !this.state.syncedPropertyValueMappings
      ) {
        return LOADING_NOTICE;
      }

      switch (this.selected.resourceType) {
        case ResourceType.Cohort:
          return this.getCohortFilterLabel(this.selected);
        case ResourceType.User:
        case ResourceType.Event:
        case ResourceType.Other:
          return this.getPropertyFilterLabel(this.selected);
        default:
          break;
      }
    }

    return ({t}) => t(`query-builder.select-property`);
  }

  private getCohortFilterLabel(filter) {
    const {filterValue, filterOperator} = filter;
    const cohortCount = filterValue?.length;

    if (!cohortCount || this.props.open) {
      return `${this.getCohortFilterOption(filterOperator)}...`;
    }

    // Safe to extract a single dataGroupId since cohorts are disabled when multiple data groups are used
    // (see shouldShowCohorts in properties-menu/index.tsx)
    const dataGroupId = getSingleDataGroupId(this.props.dataGroupIds);
    const labelParts = getCohortFilterLabelParts(filter, cohortsStore, this.state.dataGroups, dataGroupId);

    return `${labelParts.group} ${labelParts.operator} ${labelParts.value}`;
  }

  private getPropertyFilterLabel(filter: PropertyFilter) {
    const propertyItem = transformFilterToPropertyItem(filter) as Property;

    // property selection mode only displays the property name
    if (this.props.disableValueSelection && filter.resourceType !== ResourceType.Cohort) {
      return getSelectedPropertyLabel({selected: propertyItem, dataGroups: this.state.dataGroups});
    }

    const [, operator, ...labelParts] = filter.getLabelParts();
    let filterValue = [];
    if (!this.props.open) {
      filterValue = labelParts.map((part) => part.label);
    } else {
      filterValue = [`...`];
    }

    const {name} = getPropertyPath(propertyItem, this.state.dataGroups);
    const propertyName = propertiesUtils.renameProperty(name, filter.resourceType, this.props.i18n);

    return [propertyName, operator?.label, ...filterValue].filter(Boolean).join(` `);
  }

  private handleEventPropertySelect(
    item: PropertySectionItem,
    {
      autoTriggered = false,
      menuItemType = null,
      stagedItemsStack = null,
    }: {
      autoTriggered?: boolean;
      menuItemType?: MenuItemType;
      stagedItemsStack?: PropertySectionItem[];
    } = {},
  ) {
    const {value} = item;
    let behavior,
      customProperty,
      customPropertyId,
      dataGroupId,
      tempDataGroupId,
      joinPropertyType,
      propertyDefaultType,
      propertyObjectKey,
      type,
      scdEvent;
    if (isProperty(item)) {
      ({
        customProperty,
        customPropertyId,
        dataGroupId,
        tempDataGroupId,
        joinPropertyType,
        propertyDefaultType,
        propertyObjectKey,
        type,
        behavior,
        scdEvent,
      } = item);
    }

    const resourceType =
      item.value === `$multi_attribution_filter`
        ? // multi-attribution filter will have "other" resource type, but filter values will be for the attributed by
          // event property, so needs to be "event" resource type for the PropertyFilter
          ResourceType.Event
        : item.resourceType;

    // not all custom properties have a well-defined type because the formula could return
    // mixed-typed values. Use 'string' for those cases to hydrate the rest of the filter ui
    const {name, propertyType} = customProperty || {name: null, propertyType: PropertyType.String};
    const propertyFilterAttrs = {
      ...(customProperty ? {customProperty} : {}),
      ...(customPropertyId ? {customPropertyId} : {}),
      ...(dataGroupId ? {dataGroupId} : {}),
      ...(tempDataGroupId ? {tempDataGroupId} : {}),
      resourceType,
      propertyDefaultType: propertyDefaultType || propertyType,
      propertyName: value || name,
      ...(behavior ? {behavior} : {}),
      propertyType: type || propertyType,
      ...(joinPropertyType ? {joinPropertyType} : {}),
      ...(propertyObjectKey ? {propertyObjectKey} : {}),
      ...(scdEvent ? {scdEvent} : {}),
    };

    if (this.props.open) {
      // only when we are open, do we set stagedPropertyFilter, to render mp-property-values-screen
      const stagedPropertyFilter = this.createPropertyFilter(propertyFilterAttrs);
      this.setState({stagedPropertyFilter});

      // property selection mode dispatches the constructed Partial<PropertyFilter>
      if (this.props.disableValueSelection) {
        this.dispatchSelect(stagedPropertyFilter, {autoTriggered, menuItemType, stagedItemsStack});
      }
    }
  }

  private handleCohortSelect(
    selection: PropertySectionItem | PropertySectionItem[],
    {
      rawItem,
      commit = false,
      autoTriggered = false,
      menuItemType = null,
      stagedItemsStack = null,
    }: {
      rawItem: PropertySectionItem;
      commit?: boolean;
      autoTriggered?: boolean;
      menuItemType?: MenuItemType;
      stagedItemsStack?: PropertySectionItem[];
    },
  ) {
    const isSingleSelection = !Array.isArray(selection);
    const items = isSingleSelection ? [selection] : selection;

    const filterValue = items.map(getCohortFromPropertyItem);

    const filter = this.createCohortFilter({
      filterValue,
    });

    // when selecting a cohort item from the `All` tab
    // or inline cohort commit that filter selection
    if (isSingleSelection || commit || autoTriggered) {
      this.dispatchSelect(filter.getAttributes(), {rawItem, autoTriggered, stagedItemsStack, menuItemType});
    } else {
      this.setState({stagedPropertyFilter: filter});
    }
  }

  private dispatchSelect(
    item,
    {
      rawItem = null,
      autoTriggered = false,
      menuItemType = null,
      stagedItemsStack = null,
    }: {
      rawItem?: PropertySectionItem;
      autoTriggered?: boolean;
      menuItemType?: MenuItemType;
      stagedItemsStack?: PropertySectionItem[];
    } = {},
  ) {
    const trackingProps = this.trackingProps(item, rawItem);

    let queryReason;
    if (autoTriggered) {
      queryReason = QueryReason.QbAutoTriggered;
    } else {
      queryReason = QueryReason.QbFilterUpdate;
    }

    const detail = {item, trackingProps, queryReason, stagedItemsStack, menuItemType};
    this.props.onSelect?.({detail});
  }

  private onPropertyFilterSelected(filter: PropertyFilter) {
    this.dispatchSelect(filter.getAttributes());
  }

  private async syncPropertyValueMappings() {
    if (!this.sourceAttrs.projectId) {
      return;
    }
    await syncReportPropertyValueMappings(this.sourceAttrs.projectId);
    this.setState({syncedPropertyValueMappings: true});
  }

  private async fetchCohorts() {
    if (!this.props.projectHasCohorts) {
      return;
    }
    await cohortsStore.fetch({
      projectId: this.sourceAttrs.projectId,
      workspaceId: this.props.workspaceId,
    });
  }

  private async fetchDataGroups() {
    if (!this.props.allDataGroups) {
      return;
    }
    const dataGroups = await this.retrieveDataGroups();
    this.setState({dataGroups});
  }

  retrieveDataGroups(): Promise<DataGroup[]> {
    const params = this.sourceAttrs;
    return dataGroupsClient.query(params).catch((error) => {
      reporter.error(`Error loading data groups`, error);
      return [];
    });
  }

  private createPropertyFilter(attrs) {
    return PropertyFilterFactory.createPropertyFilter(attrs, {displayContext: this.displayContext});
  }

  private createCohortFilter(attrs) {
    const filterOperator = this.state.selectedCohortFilterOption || this.props.selected?.filterOperator;

    return this.createPropertyFilter({
      ...attrs,
      filterOperator: filterOperator ?? ListOperator.Contains,
      propertyType: PropertyType.List,
      resourceType: ResourceType.Cohort,
    });
  }

  private getCohortFilterOption(operator: ListOperator) {
    // Safe to extract a single dataGroupId since cohorts are disabled when multiple data groups are used
    // (see shouldShowCohorts in properties-menu/index.tsx)
    const dataGroupId = getSingleDataGroupId(this.props.dataGroupIds);
    const groupLabel = getCohortGroupLabel(this.state.dataGroups, dataGroupId);
    return `${groupLabel} ${COHORT_FILTER_OPERATOR_LABELS[operator]}`;
  }

  private trackingProps(item: PropertyFilter, rawItem: PropertySectionItem) {
    let extraProps = {};
    const isCohort = item.resourceType === ResourceType.Cohort;

    if (isCohort) {
      const cohortNames = (item.filterValue as CohortSegment[]).map((filter) => filter.cohort.name);
      extraProps = {
        // append cohort metadata
        ...(rawItem
          ? {
              'cohort type': rawItem.createdBy
                ? rawItem.createdBy.id === this.props.userId
                  ? `cohorts-yours`
                  : `cohorts`
                : `inline cohorts`,
            }
          : {}),
        'Filter value': cohortNames,
      };
    }

    return generateFilterTrackingProps(item, extraProps);
  }

  getSelectedHoverTooltipData() {
    return this.propertiesMenuRef.current?.getSelectedHoverTooltipData();
  }

  getSelectedTrackingProps() {
    return this.propertiesMenuRef.current?.getSelectedTrackingProps();
  }

  handleTooltipContextAction(ev: Event) {
    return this.propertiesMenuRef.current?.handleTooltipContextAction(ev);
  }

  canOpenMenu() {
    return this.selected && !this.props.readonly ? !isFirstTimeEventFilter(this.selected) : true;
  }

  shouldCloseOnSelect() {
    return true;
  }

  render() {
    return (
      <div className='mp-filter-menu'>
        {!this.props.disableValueSelection && this.getSelectedPropertyFilter() ? (
          <PropertyValuesScreenReact
            displayContext={this.displayContext}
            enableEnhancedListFilters={this.props.enableEnhancedListFilters}
            eventNames={this.props.eventNames}
            hasBackButton
            keydownProxyEl={this.props.keydownProxyEl}
            onChangePropertyFilter={this.onPropertyFilterChange}
            onClickBack={this.onPropertyFilterBackClick}
            onCommitPropertyFilter={this.onPropertyFilterCommit}
            projectId={this.props.projectId}
            propertyFilter={this.state.stagedPropertyFilter.getAttributes()}
            workspaceId={this.props.workspaceId}
          />
        ) : (
          <PropertiesMenuReact
            allowTypecastPillAction={this.props.disableValueSelection ? TypecastPillAction.TriggerTutorial : null}
            applyButtonText={this.props.i18n.t(`query-builder.actions.add-filter`)}
            cohortMultiselect
            cohortOperationType='filter'
            context='filter'
            dataGroupIds={this.props.dataGroupIds}
            disabledItems={this.props.disabledItems}
            enableAllDataGroupsForScdProps={this.props.enableAllDataGroupsForScdProps}
            enableWorkspaceFilterRestrictions={this.props.enableWorkspaceFilterRestrictions}
            eventNames={this.props.eventNames}
            excludeDimensionProperties={this.props.excludeDimensionProperties}
            hideEditControls={this.props.hideEditControls}
            hideEvents={this.props.hideEvents}
            includeCustomProperties={this.props.includeCustomProperties}
            inlineCohortsOverride={this.props.inlineCohortsOverride}
            listProperty={this.props.listProperty}
            menuHeader={
              this.isCohortSelection() &&
              CohortSelection({
                i18n: this.props.i18n,
                onChange: this.onCohortFilterOptionChange,
                value: this.state.selectedCohortFilterOption,
              })
            }
            metricLevelDataGroups={this.props.metricLevelDataGroups}
            minSelection={1}
            onChangeTab={this.handlePropertySelectOption}
            onSelect={this.handlePropertySelect}
            onStagedProperty={this.handleStageChange}
            onTrack={preventTracking}
            open={this.props.open}
            otherOptions={this.props.otherFilterOptions}
            projectHasCohorts={this.props.projectHasCohorts}
            projectHasPeople={this.props.projectHasPeople}
            projectId={this.props.projectId}
            readonly={this.props.readonly}
            ref={this.propertiesMenuRef}
            selected={this.getMenuSelection()}
            showBorrowedProperties={this.props.showBorrowedProperties}
            showSessionEvent
            showSpecialDatasetProperty={this.props.showSpecialDatasetProperty}
            supportB2bCohorts={this.props.supportB2bCohorts}
            trackName={this.props.trackName}
            userId={this.props.userId}
            workspaceId={this.props.workspaceId}
            allDataGroups={this.props.allDataGroups}
            disableAllScdProps={this.props.disableAllScdProps}
          />
        )}
      </div>
    );
  }
}

export const FilterMenuReact = withStyles(
  React.forwardRef<FilterMenu, FilterMenuProps>((props, ref) => {
    const currentProject = useCurrentProject();
    const i18n = useTranslation();
    return <FilterMenu {...props} ref={ref} currentProject={currentProject} i18n={i18n} />;
  }),
  css,
);
